import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ApiError } from '@dru/api-client';
import { ArrowLeft, FileText, Lock, Siren } from 'lucide-react';
import { EXAM_TYPE_LABEL, SAMPLE_STATUS_LABEL } from '@dru/types';
import type {
  DocumentGrant, LabReport, LabReportAct, LabReportPage, LabReportReadiness, LabReportRow, LabReportStatus, SampleRow, CursorPage,
} from '@dru/types';
import { FLAG_LABEL } from '@dru/types';
import {
  Alert, Button, Card, IconAction, LoadMore, ConfirmDialog, EmptyState, ErrorState, InfoTip, Pagination, SearchInput, StatusBadge, Tabs, TextSkeleton, cn,
} from '@dru/ui';
import { ProfileReports } from '../../../components/ProfileReports';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { usePages } from '../../../lib/usePages';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { useVenueFilter } from '../../hospital/usePartner';
import { sampleTone, subjectLine } from './Worklist';
import { flagTone } from '../../hospital/routes/Encounter';
import { formatDateTime } from '../../../lib/format';
import { SignatureReadiness, signaturePath, useMySignature } from '../components/SignatureReadiness';
import { useBackLink, useLabLinks } from '../lib/context';

/**
 * The laboratory report: assemble, verify, sign, download.
 *
 * Distinct from the fitness certificate, which is the physician's statutory
 * decision. This is the evidence that decision is made on, it is signed by the
 * laboratory, and it exists whether or not a certificate ever follows.
 *
 * Review-first: readiness at the top (the battery, what is out of range,
 * anything still in flight), the findings that need attention before the
 * normal ones, then the one next act for this report. Signing goes through a
 * confirmation that names exactly what is being signed and by whom, because a
 * signed revision is fixed from that moment — a correction is a numbered
 * amendment, and the original stays retrievable.
 *
 * Each act is refused to the wrong person by the server; the screen offers it
 * only to the right one. A report is never verified or signed by the person
 * who entered every value on it.
 */
const TONE: Record<LabReportStatus, 'pending' | 'info' | 'success' | 'neutral'> = {
  DRAFT: 'pending',
  READY_FOR_VERIFICATION: 'info',
  VERIFIED: 'info',
  FINAL: 'success',
  AMENDED: 'neutral',
};

/**
 * The report's state as the bench and the pathologist say it. The shared
 * labels read "Final" and "Amended"; "Amended" on the revision that an
 * amendment REPLACED read as if it were the corrected one. This screen says
 * what each one is.
 */
const REPORT_LABEL: Record<LabReportStatus, string> = {
  DRAFT: 'Draft',
  READY_FOR_VERIFICATION: 'Ready to verify',
  VERIFIED: 'Verified — to sign',
  FINAL: 'Signed',
  AMENDED: 'Superseded',
};

type View = 'sign' | 'draft' | 'signed' | 'encounters';

export function Reports() {
  const { query } = useVenueFilter();
  const { permissions } = useAuth();
  const signer = holds(permissions, ['lab.result-validate', 'lab.result-release']);
  // The exact report chosen — its id and revision live in the address, so a
  // refresh, the back button or a shared link opens that revision and never
  // silently the newest one for the encounter.
  const [params, setParams] = useSearchParams();
  const encounterId = params.get('encounter') ?? '';
  const reportId = params.get('report');
  const setSelection = (enc: string, report: string | null, rev?: number) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (enc) n.set('encounter', enc); else n.delete('encounter');
    if (report) n.set('report', report); else n.delete('report');
    if (report && rev) n.set('rev', String(rev)); else n.delete('rev');
    return n;
  }, { replace: false });
  const urlRev = Number(params.get('rev')) || null;
  // A pathologist opens on what waits for their signature; the bench on its
  // encounters, where a report is opened and prepared.
  const VIEWS: View[] = ['sign', 'draft', 'signed', 'encounters'];
  const view: View = (VIEWS as string[]).includes(params.get('view') ?? '') ? params.get('view') as View : (signer ? 'sign' : 'encounters');
  // Filters live in the address too, so a refresh or Back restores the list as it was.
  const setFilter = (k: string, v: string | null, keepPage = false) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set(k, v); else n.delete(k);
    if (!keepPage) n.delete('page');
    return n;
  }, { replace: true });
  const setView = (v: View) => setFilter('view', v);
  const SCROLL = 'dru.reports.listScroll';
  const choose = (id: string, report: string | null = null, rev?: number) => {
    try { sessionStorage.setItem(SCROLL, String(window.scrollY)); } catch { /* ignore */ }
    setSelection(id, report, rev);
    // Below lg the report replaces the list; start it at the top.
    if (!window.matchMedia('(min-width: 1024px)').matches) window.scrollTo({ top: 0 });
  };
  // Back to the list on a phone: same tab, search, page and place.
  const backToList = () => {
    setSelection('', null);
    // Restored once the list is back on screen (see the effect below).
    restoreScroll.current = true;
  };
  const back = useBackLink();
  const restoreScroll = useRef(false);
  useEffect(() => {
    if (encounterId || !restoreScroll.current) return;
    restoreScroll.current = false;
    let y = 0; try { y = Number(sessionStorage.getItem(SCROLL)) || 0; } catch { /* ignore */ }
    const t = setTimeout(() => window.scrollTo({ top: y }), 30);
    return () => clearTimeout(t);
  }, [encounterId]);

  // One tab's reports, a page at a time, searched on the server; the counts
  // for every tab come back with it, exact and for the same venue and search.
  const PAGE = 20;
  const page = Math.max(1, Number(params.get('page')) || 1);
  const setPage = (n: number) => setFilter('page', n > 1 ? String(n) : null, true);
  const term = params.get('q') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const [search, setSearch] = useState(term);
  // The box follows the address when it changes elsewhere (Back, a link).
  useEffect(() => { setSearch(term); }, [term]);
  useEffect(() => {
    const t = setTimeout(() => { if (search.trim() !== term) setFilter('q', search.trim() || null); }, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  const STATUSES: Record<Exclude<View, 'encounters'>, string> = {
    sign: 'VERIFIED,READY_FOR_VERIFICATION', draft: 'DRAFT', signed: 'FINAL',
  };
  const reports = useQuery<LabReportPage | null>(
    () => (view === 'encounters' ? Promise.resolve(null)
      : api.get<LabReportPage>(`/lab/reports?page=${page}&limit=${PAGE}&status=${STATUSES[view]}`
        + (term ? `&q=${encodeURIComponent(term)}` : '') + (from ? `&from=${from}` : '') + (to ? `&to=${to}` : '') + query)),
    [view, page, term, from, to, query],
  );
  // Counts outlive a switch to the Encounters tab: the last answer is kept.
  const [counts, setCounts] = useState<LabReportPage['counts'] | null>(null);
  useEffect(() => { if (reports.data) setCounts(reports.data.counts); }, [reports.data]);
  const n = (k: number) => (k ? String(k) : undefined);
  const toSignCount = counts ? counts.VERIFIED + counts.READY_FOR_VERIFICATION : 0;

  const samples = usePages<SampleRow>(
    (cursor) => api.get<CursorPage<SampleRow>>(`/lab/worklist?limit=50${query}${cursor ? `&cursor=${cursor}` : ''}`),
    [query],
  );

  // One entry per encounter: a report is per encounter, and two tubes from
  // one visit showed the same report twice.
  const seen = new Set<string>();
  const workable = (samples.items).filter((s) => {
    if (['REJECTED', 'RECOLLECT_REQUESTED'].includes(s.status) || seen.has(s.encounterId)) return false;
    seen.add(s.encounterId); return true;
  });

  return (
    <div className="space-y-4">
      {back && (
        <Link to={back.to} className="inline-flex min-h-8 items-center gap-1 text-small font-medium text-primary-700 hover:underline max-md:min-h-10">
          <ArrowLeft aria-hidden className="h-4 w-4" />{back.label}
        </Link>
      )}
      <PageHeader
        title="Lab reports"
        hint="Assembled once every mandatory test is released, verified by someone who did not enter it, then signed. A signed report is final — a correction is a numbered amendment and the original stays retrievable."
      />

      {signer && <SignatureReadiness act="sign" />}

      <Tabs
        tabs={[
          { key: 'sign', label: signer ? 'To verify & sign' : 'With the pathologist', hint: counts ? n(toSignCount) : undefined },
          { key: 'draft', label: 'Drafts', hint: counts ? n(counts.DRAFT) : undefined },
          { key: 'signed', label: 'Signed', hint: counts ? n(counts.FINAL) : undefined },
          { key: 'encounters', label: 'Encounters' },
        ]}
        active={view}
        onChange={(k) => setView(k as View)}
      />

      <div className="grid items-start gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        {/* Below lg a chosen report replaces the list, so it is not a long
            scroll below every encounter on a tablet. */}
        <aside className={cn('space-y-2', encounterId && 'hidden lg:block')} aria-label={view === 'encounters' ? 'Encounters' : 'Reports'}>
          {view !== 'encounters' && (<>
            <SearchInput value={search} onValueChange={setSearch} placeholder="Report no., subject, employee code or name"
                         aria-label="Search reports" className="w-full" />
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-0.5 text-caption text-neutral-600">Opened from
                <input type="date" value={from} max={to || undefined} onChange={(e) => setFilter('from', e.target.value || null)}
                       className="h-10 rounded-input border border-neutral-300 bg-white px-2 text-small text-neutral-900" />
              </label>
              <label className="flex flex-col gap-0.5 text-caption text-neutral-600">to
                <input type="date" value={to} min={from || undefined} onChange={(e) => setFilter('to', e.target.value || null)}
                       className="h-10 rounded-input border border-neutral-300 bg-white px-2 text-small text-neutral-900" />
              </label>
            </div>
            <ReportList
              rows={reports.data?.items ?? []}
              loading={reports.loading && !reports.data}
              error={reports.error}
              onRetry={reports.reload}
              selected={reportId ?? ''}
              onChoose={(r) => choose(r.encounterId, r.id, r.revision)}
              empty={view === 'sign'
                ? { title: 'Nothing waiting for a signature', hint: 'A report arrives here once every mandatory result is released and the bench prepares it.' }
                : view === 'draft'
                  ? { title: 'No draft reports', hint: 'A report stays a draft until its whole mandatory battery is released. Open one from By encounter.' }
                  : { title: 'No signed reports yet', hint: 'Reports appear here once a pathologist releases and signs them.' }}
              searched={!!(term || from || to)}
            />
            {reports.data && reports.data.total > PAGE && (
              <Pagination page={page} pageSize={PAGE} total={reports.data.total} onPageChange={setPage} disabled={reports.loading} />
            )}
          </>)}
          {view === 'encounters' && (<>
          <h2 className="px-1 text-micro uppercase tracking-wider text-neutral-500">Encounters · {workable.length}</h2>
          {samples.loading && !samples.items.length && <TextSkeleton lines={4} />}
          {samples.error && (
            <ErrorState title="Could not load samples" detail={samples.error} onRetry={samples.reload} />
          )}
          {!samples.loading && !workable.length && (
            <EmptyState title="Nothing to report on" hint="An encounter appears once phlebotomy draws its first tube." />
          )}
          <ul className="space-y-1.5">
            {workable.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => choose(s.encounterId)}
                  aria-current={s.encounterId === encounterId ? 'true' : undefined}
                  className={cn(
                    'w-full rounded-input border px-3 py-2 text-left transition-colors duration-hover',
                    s.encounterId === encounterId
                      ? 'border-primary-600 bg-primary-50 ring-1 ring-primary-600'
                      : 'border-neutral-200 bg-white hover:bg-neutral-50',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-small text-neutral-900">{s.subject.displayName}</span>
                    <StatusBadge tone={sampleTone(s.status)}>Tube: {SAMPLE_STATUS_LABEL[s.status] ?? s.status}</StatusBadge>
                  </div>
                  <div className="mt-0.5 text-caption font-normal text-neutral-500">
                    {subjectLine(s.subject) || 'Sex and age not recorded'} · <span className="font-mono">{s.barcode}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <LoadMore hasMore={samples.hasMore} loading={samples.loadingMore} onLoadMore={samples.loadMore} shown={samples.items.length} />
          </>)}
        </aside>

        <section className={cn('min-w-0', !encounterId && 'hidden lg:block')}>
          {encounterId && (
            <Button variant="ghost" size="sm" className="mb-2 lg:hidden" icon={<ArrowLeft aria-hidden className="h-4 w-4" />} onClick={backToList}>
              Back to list
            </Button>
          )}
          {encounterId
            ? (
              <div className="space-y-4">
                <ReportPanel encounterId={encounterId} reportId={reportId} expectedRevision={urlRev}
                  onOpen={(id, rev) => setSelection(encounterId, id, rev)} onChanged={reports.reload} />
                <ProfileReports audience={{ kind: 'venue', encounterId, canDownload: holds(permissions, 'lab.report-download') }} />
              </div>
            )
            : (
              <EmptyState
                title={view === 'encounters' ? 'Choose an encounter' : 'Choose a report'}
                hint="Its findings, what it still needs and the next step appear here."
              />
            )}
        </section>
      </div>
    </div>
  );
}


/** Reports in one state, newest first, each opening its encounter's report. */
function ReportList({ rows, loading, error, onRetry, selected, onChoose, empty, searched }: {
  rows: LabReportRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** The chosen report's id. */
  selected: string;
  onChoose: (row: LabReportRow) => void;
  empty: { title: string; hint: string };
  searched?: boolean;
}) {
  if (loading) return <TextSkeleton lines={4} />;
  if (error) return <ErrorState title="Could not load reports" detail={error} onRetry={onRetry} />;
  if (!rows.length) {
    return searched
      ? <EmptyState title="No report matches" hint="Search by report number, employee code or name." />
      : <EmptyState title={empty.title} hint={empty.hint} />;
  }
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            onClick={() => onChoose(r)}
            aria-current={r.id === selected ? 'true' : undefined}
            className={cn(
              'w-full rounded-input border px-3 py-2 text-left transition-colors duration-hover',
              r.id === selected
                ? 'border-primary-600 bg-primary-50 ring-1 ring-primary-600'
                : 'border-neutral-200 bg-white hover:bg-neutral-50',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="min-w-0 truncate text-small font-medium text-neutral-900">{r.subject.fullName}</span>
              <StatusBadge tone={TONE[r.status]}>{REPORT_LABEL[r.status]}</StatusBadge>
            </div>
            <div className="mt-0.5 text-caption font-normal text-neutral-500">
              <span className="font-mono">{r.reportNumber}</span>
              {r.subjectLabel && <span className="font-mono"> · {r.subjectLabel}</span>}
              {r.revision > 1 && <span className="font-medium text-neutral-700"> · amendment, revision {r.revision}</span>}
            </div>
            <div className="text-caption font-normal text-neutral-500">
              {r.status === 'FINAL' ? `Signed ${onDateTime(r.finalisedAt)}`
                : r.status === 'VERIFIED' ? `Verified ${onDateTime(r.verifiedAt)}`
                  : `Opened ${onDateTime(r.createdAt)}`}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

const onDateTime = (d: string | null | undefined) => formatDateTime(d);

const kb = (n: number | null | undefined) => (n == null ? '—' : `${Math.max(1, Math.round(n / 1024))} KB`);

const isCritical = (f: string) => f === 'CRITICAL_LOW' || f === 'CRITICAL_HIGH';

/**
 * Downloading fetches a short-lived grant and follows it, rather than holding
 * a permanent URL. The link is dead within minutes — a medical document's URL
 * outliving the click is how one ends up in a chat history.
 *
 * "View" opens the same grant inline in a new tab; "Download" saves it under
 * the report number. Two verbs, one grant, one audit row each.
 */
export async function openReport<G extends DocumentGrant = DocumentGrant>(grantPath: string, mode: 'view' | 'download'): Promise<G> {
  const grant = await api.get<G>(grantPath);
  if (mode === 'view') {
    window.open(`${grant.url}?inline=1`, '_blank', 'noopener');
    return grant;
  }
  const res = await fetch(grant.url, { credentials: 'include' });
  if (!res.ok) throw new Error('The document could not be fetched.');
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = `${grant.reportNumber.replace(/\//g, '-')}.pdf`;
  a.click();
  URL.revokeObjectURL(href);
  return grant;
}

function ReportPanel({ encounterId, reportId, expectedRevision, onOpen, onChanged }: {
  encounterId: string;
  /** The exact report chosen; null opens the encounter's newest revision. */
  reportId?: string | null;
  /** The revision the link named, to say so if it is not what is shown. */
  expectedRevision?: number | null;
  onOpen?: (id: string, revision: number) => void;
  onChanged?: () => void;
}) {
  const { permissions, principal } = useAuth();
  const { pathname } = useLocation();
  const links = useLabLinks();
  // What the signature box on the PDF will carry, read before the signature.
  const mine = useMySignature();
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState<'sign' | 'verify-and-sign' | null>(null);
  const [amending, setAmending] = useState(false);
  const [showNormal, setShowNormal] = useState(false);

  // The newest revision and how many there are, from the server.
  const list = useQuery<LabReportPage>(
    () => api.get(`/lab/reports?encounterId=${encounterId}&page=1&limit=1`),
    [encounterId, nonce],
  );
  const latest = list.data?.items[0] ?? null;
  const revisions = list.data?.total ?? 0;
  // The revision the reader chose, by id — the newest only when they chose
  // the encounter rather than a report.
  const currentId = reportId ?? latest?.id ?? null;
  // Every earlier revision, paged — never cut off at a fixed number.
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY = 10;
  const historyQ = useQuery<LabReportPage | null>(
    () => (revisions > 1 ? api.get(`/lab/reports?encounterId=${encounterId}&page=${historyPage}&limit=${HISTORY}`) : Promise.resolve(null)),
    [encounterId, historyPage, revisions, nonce],
  );
  const history = (historyQ.data?.items ?? []).filter((r) => r.id !== currentId);

  const detail = useQuery<LabReport | null>(
    () => (currentId ? api.get(`/lab/reports/${currentId}`) : Promise.resolve(null)),
    [currentId, nonce],
  );
  const current = detail.data;
  // Signing is not the PDF: a signed revision's document is rendered after
  // the signature. While it is PENDING the report is re-read every few
  // seconds (for up to two minutes) until it is ready or the failure is
  // recorded — never shown as ready because the signature went through.
  const [polls, setPolls] = useState(0);
  const pdfPending = current?.status === 'FINAL' && current.generationStatus === 'PENDING';
  useEffect(() => {
    if (!pdfPending || polls >= 40) return;
    const t = setTimeout(() => { setPolls((n) => n + 1); detail.reload(); }, 3000);
    return () => clearTimeout(t);
  }, [pdfPending, polls]); // eslint-disable-line react-hooks/exhaustive-deps

  const canVerify = holds(permissions, 'lab.result-validate');
  const canFinalise = holds(permissions, 'lab.result-release');
  const canAmend = holds(permissions, 'lab.result-amend');
  const canDownload = holds(permissions, 'lab.report-download');
  // Assembling a report needs result-entry rights (the route's permission).
  const canEnter = holds(permissions, 'lab.result-enter');

  // A refusal because the report changed since it was read reloads it, so
  // the reader sees what changed before trying again.
  const changed = (e: unknown) => e instanceof ApiError && e.type === 'lab-report-changed';
  async function act(path: string, body?: unknown) {
    setBusy(path); setError(null);
    try {
      await api.post(path, body ?? {}, crypto.randomUUID());
      setNonce((n) => n + 1);
      onChanged?.();
    } catch (e) {
      setError(errorMessage(e));
      if (changed(e)) setNonce((n) => n + 1);
    } finally { setBusy(null); }
  }
  /** What the reader is looking at, sent with every act on it. */
  const seen = () => (current ? { expectedRevision: current.revision, ...(current.contentSha256 ? { contentSha256: current.contentSha256 } : {}) } : {});

  async function open(id: string, mode: 'view' | 'download') {
    setBusy(`${mode}-${id}`); setError(null);
    try { await openReport(`/lab/reports/${id}/download`, mode); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(null); }
  }

  async function sign(alsoVerify: boolean) {
    if (!current) return;
    setBusy('sign'); setError(null);
    try {
      if (alsoVerify) await api.post(`/lab/reports/${current.id}/verify`, seen(), crypto.randomUUID());
      await api.post(`/lab/reports/${current.id}/finalise`, seen(), crypto.randomUUID());
      setSigning(null);
      setNonce((n) => n + 1);
      onChanged?.();
    } catch (e) {
      setError(errorMessage(e)); setSigning(null);
      if (changed(e)) setNonce((n) => n + 1);
    } finally { setBusy(null); }
  }

  if (list.loading) return <TextSkeleton lines={6} />;
  if (list.error) {
    return <ErrorState title="Could not load reports" detail={list.error} onRetry={list.reload} />;
  }

  const finalising = busy === 'sign';
  const generating = finalising || (current?.status === 'FINAL' && current.generationStatus === 'PENDING');
  const signed = current?.status === 'FINAL' || current?.status === 'AMENDED';
  const attention = (current?.lines ?? []).filter((l) => l.flag !== 'NORMAL')
    .sort((x, y) => Number(isCritical(y.flag)) - Number(isCritical(x.flag)));
  const normal = (current?.lines ?? []).filter((l) => l.flag === 'NORMAL');
  const critical = attention.filter((l) => isCritical(l.flag)).length;
  const readiness = current?.readiness ?? [];
  const stops = (act: LabReportAct, except: string[] = []) =>
    readiness.filter((r) => r.blocks.includes(act) && !except.includes(r.code));
  const advice = readiness.filter((r) => !r.blocks.length);
  const summary = current?.readinessSummary ?? null;
  // A permitted next action, as a link to the screen that takes it. The
  // server only sends one the reader may take.
  // Each carries the subject (and the specimen, when the blocker names one)
  // and a way back to this exact report, so nobody searches for it again.
  const actionHref = (item: LabReportReadiness): string | null => {
    const a = item.permittedNextAction;
    if (!a) return null;
    const sample = item.entity === 'specimen' ? item.affectedEntityIds[0] : undefined;
    switch (a.action) {
      case 'OPEN_RELEASE': return links.to('release', { subject: encounterId, back: true });
      case 'OPEN_SAMPLES': return links.to('samples', { subject: encounterId, sample, back: true });
      case 'OPEN_RESULT_ENTRY': return links.to('results', { subject: encounterId, back: true });
      case 'OPEN_SIGNATURE': return `${signaturePath(pathname)}?back=${encodeURIComponent(links.here)}`;
      default: return null;
    }
  };
  const canOpenSignature = holds(permissions, 'signature.manage-own');
  const normalOpen = showNormal || attention.length === 0;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger">{error}</Alert>}

      {!currentId && (
        <Card className="p-4">
          <p className="flex items-center gap-1 text-body text-neutral-800">
            No report has been opened for this encounter.
            <InfoTip label="About opening a report">
              Opening one collects every released result. It stays a draft until the whole mandatory battery is in.
            </InfoTip>
          </p>
          {!canEnter && <p className="mt-1 text-small text-neutral-500">The laboratory bench opens it.</p>}
          {/* Opening (assembling) is the bench's act — the route needs
              result-entry rights, and a pathologist was offered a 403. */}
          {canEnter && (
            <Button
              className="mt-3"
              loading={busy === `/lab/encounters/${encounterId}/report`}
              onClick={() => act(`/lab/encounters/${encounterId}/report`)}
            >
              Open report
            </Button>
          )}
        </Card>
      )}

      {currentId && detail.loading && !current && <TextSkeleton lines={6} />}

      {current && current.encounterId !== encounterId && (
        <Alert tone="warning">The report in the link belongs to another encounter; it is shown as it is.</Alert>
      )}
      {current && latest && current.id !== latest.id && (
        <Alert tone="info" title={`You are reading revision ${current.revision}`}>
          Revision {latest.revision} ({REPORT_LABEL[latest.status]}) is the newest for this encounter.{' '}
          {onOpen && <button type="button" className="font-semibold underline underline-offset-2" onClick={() => onOpen(latest.id, latest.revision)}>Open revision {latest.revision}</button>}
        </Alert>
      )}
      {current && reportId && expectedRevision && current.revision !== expectedRevision && (
        <Alert tone="warning">The link named revision {expectedRevision}; this report is revision {current.revision}.</Alert>
      )}
      {reportId && detail.error && (
        <Alert tone="warning">The report in the link could not be opened — it may not exist, or it is not this venue's.</Alert>
      )}

      {current && (
        <>
          <Card>
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-neutral-200 px-4 py-3">
              <div className="min-w-0">
                <div className="text-caption text-neutral-500">
                  Lab report{current.revision > 1 ? ` · revision ${current.revision}` : ''}
                </div>
                <h2 className="break-all font-mono text-h4 text-neutral-900">{current.reportNumber}</h2>
                <p className="text-small text-neutral-600">
                  {current.encounter?.order.employee.fullName ?? 'Subject'}
                  {current.encounter?.order.employee.employeeCode ? ` · ${current.encounter.order.employee.employeeCode}` : ''}
                  {current.partner ? ` · ${current.partner.name}${current.partner.city ? `, ${current.partner.city}` : ''}` : ''}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge tone={TONE[current.status]}>{REPORT_LABEL[current.status]}</StatusBadge>
                {/* Signed and Superseded already say what they are; a working
                    revision also says whether it is the current one. */}
                {!signed && (
                  <StatusBadge tone="info">{revisionKind(current, latest?.id ?? null)}</StatusBadge>
                )}
              </div>
            </div>

            {/* A signed revision reads as what it is: fixed. Nothing on the
                screen suggests editing it; the only way on is an amendment. */}
            {signed && (
              <div className={cn(
                'flex items-start gap-2 border-b px-4 py-2.5 text-small',
                current.status === 'FINAL' ? 'border-success-600/25 bg-success-100 text-success-600' : 'border-neutral-200 bg-neutral-50 text-neutral-700',
              )}
              >
                <Lock aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {current.status === 'FINAL'
                    ? <><span className="font-semibold">Signed and final.</span> Corrections are made as an amendment; this revision stays on record.</>
                    : <><span className="font-semibold">Superseded</span> by a later amendment. Kept exactly as signed.</>}
                </span>
              </div>
            )}
            {current.revision > 1 && current.amendmentReason && (
              <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-2 text-small text-neutral-700">
                <span className="font-semibold text-neutral-900">Amendment — revision {current.revision}.</span> Reason: {current.amendmentReason}
              </div>
            )}

            {/* Readiness, read before the signature rather than after a refusal.
                The server refuses finalisation on the same checks. */}
            {/* Each count from typed data: results, specimens and corrections
                are counted as what they are; blocking issues as issues. */}
            <ul aria-label="Report readiness" className="grid grid-cols-2 gap-2 px-4 py-3 sm:grid-cols-3 xl:grid-cols-6">
              <Ready
                label="Mandatory results released"
                value={`${current.completeness.completed} / ${current.completeness.required}`}
                tone={current.completeness.missing.length ? 'warning' : 'success'}
              />
              <Ready label="Outside range" value={String(attention.length)} tone={attention.length ? 'warning' : 'neutral'} />
              <Ready label="Critical" value={critical ? `${critical} critical` : '0'} tone={critical ? 'danger' : 'neutral'} icon={critical > 0} />
              {!signed && summary && (<>
                <Ready label="Pending specimens" value={String(summary.pendingSpecimens)} tone={summary.pendingSpecimens ? 'warning' : 'success'} />
                <Ready label="Pending corrections" value={String(summary.pendingCorrections)} tone={summary.pendingCorrections ? 'warning' : 'success'} />
                <Ready label="Blocking issues" value={String(summary.blockingIssues)} tone={summary.blockingIssues ? 'warning' : 'success'} />
              </>)}
            </ul>
            {!signed && summary && (canVerify || canFinalise) && (
              <p className="px-4 pb-3 text-small text-neutral-700">
                <span className="font-medium">Your signing eligibility: </span>
                {canVerify && <>{summary.signing.canVerify ? 'can verify' : 'cannot verify yet'}</>}
                {canVerify && canFinalise && ' · '}
                {canFinalise && <>{summary.signing.canFinalise ? 'can sign' : 'cannot sign yet'}</>}
                {(!summary.signing.canVerify || !summary.signing.canFinalise) && ' — the reasons are listed below.'}
              </p>
            )}

            {!signed && current.completeness.missing.length > 0 && (
              <div className="px-4 pb-3">
                <Alert tone="warning">
                  {current.completeness.missing.map((m) => m.name).join(', ')} {current.completeness.missing.length === 1 ? 'is' : 'are'} still owed.
                  The report moves on by itself when the last result is released.
                </Alert>
              </div>
            )}
            {!signed && readiness.some((r) => r.blocks.length && r.code !== 'BATTERY_INCOMPLETE') && (
              <div className="px-4 pb-3">
                <ReadinessList title="Before this report can move on" items={readiness.filter((r) => r.blocks.length && r.code !== 'BATTERY_INCOMPLETE')} href={actionHref} />
              </div>
            )}
          </Card>

          <Card>
            {attention.length > 0 && (
              <section aria-label="Findings needing attention">
                <h3 className="bg-neutral-50 px-4 py-1.5 text-micro uppercase tracking-wider text-neutral-600">
                  Needs attention · {attention.length}
                </h3>
                <ul className="divide-y divide-neutral-200">
                  {attention.map((l, i) => <Line key={`${l.testCode}-${i}`} l={l} emphasis />)}
                </ul>
              </section>
            )}
            {normal.length > 0 && (
              <section aria-label="Normal results" className={cn(attention.length > 0 && 'border-t border-neutral-200')}>
                <button
                  type="button"
                  onClick={() => setShowNormal((v) => !v)}
                  aria-expanded={normalOpen}
                  className="flex w-full items-center justify-between px-4 py-2 text-left hover:bg-neutral-50"
                >
                  <span className="text-micro uppercase tracking-wider text-neutral-600">Normal · {normal.length}</span>
                  <span className="text-caption text-neutral-500">{normalOpen ? 'Hide' : 'Show'}</span>
                </button>
                {normalOpen && (
                  <ul className="divide-y divide-neutral-200 border-t border-neutral-200">
                    {normal.map((l, i) => <Line key={`${l.testCode}-${i}`} l={l} />)}
                  </ul>
                )}
              </section>
            )}
            {!current.lines.length && (
              <p className="px-4 py-3 text-small text-neutral-500">Nothing has been released for this encounter yet.</p>
            )}
          </Card>

          {/* The one next act for this report, for the person who may take it. */}
          <Card className="px-4 py-3">
            <div className="text-caption text-neutral-500">{signed ? 'Signed document' : 'Next step'}</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {current.status === 'DRAFT' && !canEnter && (
                // Preparing a draft is the bench's act (the route needs
                // result-entry rights). The signer reloads and reads.
                <>
                  <Button variant="secondary" onClick={() => setNonce((n) => n + 1)}>Reload</Button>
                  <span className="text-small text-neutral-600">
                    {current.completeness.missing.length
                      ? 'Draft — waiting on the rest of the mandatory battery.'
                      : 'Draft — every result is in; the laboratory bench prepares it for verification.'}
                  </span>
                </>
              )}
              {current.status === 'DRAFT' && canEnter && (current.completeness.missing.length === 0 ? (
                // Every mandatory result is in — typically a freshly opened
                // amendment. Assembling it is what moves it to verification.
                <Button
                  loading={busy === `/lab/encounters/${encounterId}/report`}
                  disabled={stops('prepare').length > 0}
                  onClick={() => act(`/lab/encounters/${encounterId}/report`)}
                >
                  Prepare {current.revision > 1 ? `revision ${current.revision}` : 'report'} for verification
                </Button>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    loading={busy === `/lab/encounters/${encounterId}/report`}
                    onClick={() => act(`/lab/encounters/${encounterId}/report`)}
                  >
                    Refresh
                  </Button>
                  <span className="text-small text-neutral-600">
                    {current.completeness.missing.length
                      ? 'Waiting on the rest of the mandatory battery.'
                      : 'Every result is in — waiting for the laboratory to prepare it for verification.'}
                  </span>
                </>
              ))}
              {current.status === 'READY_FOR_VERIFICATION' && (canVerify ? (
                <>
                  <Button
                    variant="secondary"
                    loading={busy === `/lab/reports/${current.id}/verify`}
                    disabled={stops('verify').length > 0}
                    onClick={() => act(`/lab/reports/${current.id}/verify`, seen())}
                  >
                    Verify report
                  </Button>
                  {/* The pathologist who verifies usually also signs, minutes
                      later — one act where both are held, through the same
                      confirmation as signing alone. */}
                  {canFinalise && (
                    <Button variant="success" disabled={stops('verify').length > 0 || stops('finalise', ['NEEDS_VERIFICATION']).length > 0} onClick={() => setSigning('verify-and-sign')}>
                      Verify, release &amp; sign…
                    </Button>
                  )}
                </>
              ) : <span className="text-small text-neutral-600">Waiting for a pathologist to verify it.</span>)}
              {current.status === 'VERIFIED' && (canFinalise ? (
                <Button variant="success" loading={finalising} disabled={stops('finalise').length > 0} onClick={() => setSigning('sign')}>
                  {finalising ? 'Signing and generating…' : 'Release & sign report…'}
                </Button>
              ) : <span className="text-small text-neutral-600">Verified — waiting for a pathologist to release and sign it.</span>)}
              {current.hasDocument && canDownload && !pdfPending && current.generationStatus !== 'GENERATION_FAILED' && (
                <>
                  <Button
                    variant="secondary"
                    loading={busy === `view-${current.id}`}
                    onClick={() => open(current.id, 'view')}
                  >
                    View report
                  </Button>
                  <Button
                    variant="ghost"
                    loading={busy === `download-${current.id}`}
                    onClick={() => open(current.id, 'download')}
                  >
                    Download PDF
                  </Button>
                </>
              )}
              {current.status === 'FINAL' && current.generationStatus === 'GENERATION_FAILED' && canFinalise && (
                <Button
                  variant="secondary"
                  loading={busy === `/lab/reports/${current.id}/regenerate`}
                  onClick={() => act(`/lab/reports/${current.id}/regenerate`)}
                >
                  Retry PDF
                </Button>
              )}
              {current.status === 'FINAL' && canAmend && (
                <Button variant="ghost" onClick={() => setAmending(true)}>Open an amendment…</Button>
              )}
            </div>
            {signed && (
              // The document's own state, apart from the signature's.
              <p className="mt-2 flex flex-wrap items-center gap-2 text-small" aria-live="polite">
                <span className="text-neutral-500">PDF:</span>
                {generating || pdfPending
                  ? <StatusBadge tone="info">Generating…</StatusBadge>
                  : current.generationStatus === 'GENERATION_FAILED'
                    ? <StatusBadge tone="danger">Failed{canFinalise ? ' — retry above' : ' — a pathologist can retry it'}</StatusBadge>
                    : current.hasDocument
                      ? <StatusBadge tone="success">Ready to view and download</StatusBadge>
                      : <StatusBadge tone="neutral">Not generated</StatusBadge>}
                {pdfPending && polls >= 40 && <span className="text-caption text-neutral-600">Still generating — <button type="button" className="font-semibold underline" onClick={() => { setPolls(0); detail.reload(); }}>check again</button></span>}
              </p>
            )}
            {current.generationError && <p className="mt-2 text-caption text-danger-600">{current.generationError}</p>}
            {/* Why the next act is not available, and who takes it — the
                responsible role only; nobody is named who was not assigned. */}
            {!signed && (() => {
              const act: LabReportAct | null = current.status === 'DRAFT' ? 'prepare'
                : current.status === 'READY_FOR_VERIFICATION' ? 'verify'
                  : current.status === 'VERIFIED' ? 'finalise' : null;
              const why = act ? stops(act) : [];
              return why.length > 0 ? <div className="mt-3"><ReadinessList title="Waiting on" items={why} href={actionHref} /></div> : null;
            })()}
            {!signed && advice.length > 0 && (
              <div className="mt-3 space-y-1">
                {advice.map((a) => (
                  <p key={a.code} className="text-caption text-warning-600">
                    {a.message} {a.why}{' '}
                    {a.permittedNextAction && canOpenSignature && (
                      <Link to={signaturePath(pathname)} className="font-semibold underline underline-offset-2">{a.permittedNextAction.label}</Link>
                    )}
                  </p>
                ))}
              </div>
            )}
          </Card>

          {signed && (
            <Card className="px-4 py-3">
              <h3 className="text-body font-semibold text-neutral-900">Signature record</h3>
              <dl className="mt-2 grid gap-x-6 gap-y-1.5 text-small sm:grid-cols-2">
                <Sig k="Entered by" v={current.signatures.enteredBy.join(', ') || 'Not recorded'} />
                <Sig k="Verified by" v={`${current.signatures.verifiedBy ?? 'Name not recorded'} · ${onDateTime(current.signatures.verifiedAt)}`} />
                <Sig
                  k="Signed by"
                  v={`${current.signatures.finalisedBy ?? 'Name not recorded'}${current.signatures.signerRole ? ` (${current.signatures.signerRole})` : ''} · ${onDateTime(current.signatures.finalisedAt)}`}
                />
                <Sig k="Revision" v={`${current.revision}${current.status === 'AMENDED' ? ' — superseded' : ''}`} />
                {current.signedPayloadSha256 && (
                  <Sig k="Content hash" v={`sha256 ${current.signedPayloadSha256.slice(0, 16)}…`} mono />
                )}
                {current.document && (
                  <Sig k="Document" v={`PDF · ${kb(current.document.sizeBytes)} · sha256 ${current.document.sha256?.slice(0, 16) ?? '—'}…`} mono />
                )}
              </dl>
            </Card>
          )}
        </>
      )}

      {history.length > 0 && (
        <Card className="px-4 py-3">
          <h3 className="text-micro uppercase tracking-wider text-neutral-500">Other revisions · {revisions - 1}</h3>
          <ul className="mt-2 divide-y divide-neutral-200 text-small">
            {history.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <button type="button" onClick={() => onOpen?.(r.id, r.revision)} className="text-left font-mono text-neutral-800 hover:text-primary-700 hover:underline">
                  {r.reportNumber}<span className="ml-2 font-sans text-neutral-500">revision {r.revision}</span>
                </button>
                <span className="flex items-center gap-2">
                  <StatusBadge tone={r.status === 'AMENDED' ? 'neutral' : r.status === 'FINAL' ? 'success' : 'info'}>{revisionKind(r, latest?.id ?? null)}</StatusBadge>
                  {r.hasDocument && canDownload && (
                    <IconAction label={`View PDF of ${r.reportNumber} revision ${r.revision}`} icon={FileText} onClick={() => open(r.id, 'view')} />
                  )}
                </span>
              </li>
            ))}
          </ul>
          {(historyQ.data?.total ?? 0) > HISTORY && (
            <Pagination className="mt-2" page={historyPage} pageSize={HISTORY} total={historyQ.data!.total} onPageChange={setHistoryPage} />
          )}
        </Card>
      )}

      {signing && current && (
        <ConfirmDialog
          title="Release & sign lab report"
          what="Releasing creates an immutable signed report revision. Corrections require a new amended revision."
          affects={
            <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-0.5">
              <dt className="text-neutral-500">Employee</dt>
              <dd>{current.encounter?.order.employee.fullName ?? '—'} · {current.encounter?.order.employee.employeeCode ?? '—'}</dd>
              <dt className="text-neutral-500">Case</dt>
              <dd>{EXAM_TYPE_LABEL[current.encounter?.order.examinationType ?? ''] ?? current.encounter?.order.examinationType ?? '—'}</dd>
              <dt className="text-neutral-500">Lab</dt>
              <dd>{current.partner ? `${current.partner.name}${current.partner.city ? `, ${current.partner.city}` : ''}` : '—'}</dd>
              <dt className="text-neutral-500">Report</dt>
              <dd className="break-all font-mono">{current.reportNumber}</dd>
              <dt className="text-neutral-500">Revision</dt>
              <dd>{current.revision}{current.revision > 1 ? ' (amendment)' : ''}</dd>
              <dt className="text-neutral-500">Findings</dt>
              <dd>{attention.length} outside range · {critical} critical · {current.lines.length} tests</dd>
              <dt className="text-neutral-500">Signer</dt>
              <dd>{principal?.fullName ?? '—'}{(principal?.roleNames ?? []).length ? ` · ${principal!.roleNames!.join(', ')}` : ''}</dd>
              <dt className="text-neutral-500">Registration</dt>
              <dd className="text-neutral-600">
                {mine.data?.professional.registrationNo
                  ? `${mine.data.professional.registrationNo}${mine.data.professional.registrationCouncil ? ` · ${mine.data.professional.registrationCouncil}` : ''}`
                  : 'Not recorded on this account'}
              </dd>
              <dt className="text-neutral-500">Signature</dt>
              <dd className={cn(mine.data && !mine.data.current ? 'text-warning-600' : 'text-neutral-600')}>
                {!mine.data ? '—' : mine.data.current ? `Image on file (version ${mine.data.current.version})` : 'No image on file — prints “signed electronically”'}
              </dd>
            </dl>
          }
          irreversible="The signed revision, its content hash and its PDF are fixed from this moment. Nothing on it can be edited afterwards."
          confirmLabel="Release & sign report"
          tone="success"
          onConfirm={() => sign(signing === 'verify-and-sign')}
          onCancel={() => setSigning(null)}
        />
      )}

      {amending && current && (
        <ConfirmDialog
          title={`Amend ${current.reportNumber}`}
          what={`Revision ${current.revision + 1} is opened as a draft. Revision ${current.revision} stays exactly as signed, and is marked superseded once the amendment is signed.`}
          irreversible="The reason is printed on the amended report and kept on the record."
          requireReason
          reasonLabel="Why is this report being amended?"
          reasonHint="At least ten characters. Printed on the amended report."
          confirmLabel="Open amendment"
          onConfirm={async (reason) => {
            if (reason.length < 10) throw new Error('Say why in at least ten characters.');
            await api.post(`/lab/reports/${current.id}/amend`, { reason, expectedRevision: current.revision }, crypto.randomUUID());
            setAmending(false);
            setNonce((n) => n + 1);
            onChanged?.();
          }}
          onCancel={() => setAmending(false)}
        />
      )}
    </div>
  );
}

/**
 * What a revision is, in words that do not contradict each other: the one
 * still being worked on, a signed one standing, or one an amendment replaced.
 */
function revisionKind(r: { id: string; status: LabReportStatus }, latestId: string | null): string {
  if (r.status === 'AMENDED') return 'Superseded';
  if (r.status === 'FINAL') return 'Signed';
  return r.id === latestId ? 'Current working' : 'Earlier draft';
}

/** Each thing in the way, the role that acts on it, in the words the server uses. */
function ReadinessList({ title, items, href }: {
  title: string; items: LabReportReadiness[];
  href: (item: LabReportReadiness) => string | null;
}) {
  return (
    <div className="rounded-input border border-warning-600/30 bg-warning-100 px-3 py-2">
      <p className="text-caption font-semibold text-warning-600">{title}</p>
      <ul className="mt-1 space-y-1.5">
        {items.map((r) => {
          const to = r.permittedNextAction ? href(r) : null;
          return (
            <li key={r.code} className="text-small text-neutral-800">
              <div className="flex flex-wrap items-baseline gap-x-2">
                {/* The responsible role — nobody is named who was not assigned. */}
                <span className="shrink-0 rounded-badge bg-white px-1.5 text-caption font-medium text-neutral-700">{r.role}</span>
                <span className="min-w-0">{r.message}</span>
              </div>
              <p className="text-caption text-neutral-600">
                {r.why}
                {r.permittedNextAction && to && <>{' '}<Link to={to} className="font-semibold text-primary-700 underline underline-offset-2">{r.permittedNextAction.label}</Link></>}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Ready({ label, value, tone, icon }: { label: string; value: string; tone: 'success' | 'warning' | 'danger' | 'neutral'; icon?: boolean }) {
  return (
    <li className={cn(
      'rounded-input border px-3 py-2',
      tone === 'danger' ? 'border-danger-600/40 bg-danger-100' : tone === 'warning' ? 'border-warning-600/30' : 'border-neutral-200',
    )}
    >
      <div className={cn('text-caption', tone === 'danger' ? 'text-danger-600' : 'text-neutral-500')}>{label}</div>
      <div className={cn(
        'flex items-center gap-1 text-body font-semibold',
        tone === 'danger' ? 'text-danger-600' : tone === 'warning' ? 'text-warning-600'
          : tone === 'success' ? 'text-success-600' : 'text-neutral-900',
      )}
      >
        {icon && <Siren aria-hidden className="h-4 w-4" />}
        {value}
      </div>
    </li>
  );
}

function Line({ l, emphasis }: { l: LabReport['lines'][number]; emphasis?: boolean }) {
  return (
    <li className={cn('px-4 py-2.5', isCritical(l.flag) && 'bg-danger-100/50')}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <div className={cn('text-body text-neutral-900', emphasis && 'font-semibold')}>{l.testName}</div>
          <div className="font-mono text-caption font-normal text-neutral-500">
            {l.testCode}{l.loincCode ? ` · LOINC ${l.loincCode}` : ''}{l.group ? ` · ${l.group}` : ''}
            {l.referenceRange && <span className="ml-2 font-sans">ref {l.referenceRange}</span>}
            {l.criticalRange && <span className="ml-2 font-sans text-danger-600">critical {l.criticalRange}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className={cn(
            'text-neutral-900',
            l.valueType === 'TEXT' ? 'max-w-xs whitespace-pre-wrap text-small' : 'tabular-nums',
            emphasis && l.valueType !== 'TEXT' && 'text-body-lg font-semibold',
          )}
          >
            {l.value}{l.unit && l.valueType !== 'TEXT' ? <span className="ml-1 text-small font-normal text-neutral-500">{l.unit}</span> : null}
          </span>
          <StatusBadge tone={flagTone(l.flag)}>
            {isCritical(l.flag) && <Siren aria-hidden className="h-3.5 w-3.5" />}
            {FLAG_LABEL[l.flag]}
          </StatusBadge>
          {l.amended && <StatusBadge tone="warning">Amended value</StatusBadge>}
        </div>
      </div>
    </li>
  );
}

function Sig({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex min-w-0 gap-2">
      <dt className="w-28 shrink-0 text-neutral-500">{k}</dt>
      <dd className={cn('min-w-0 break-words text-neutral-800', mono && 'font-mono text-caption')}>{v}</dd>
    </div>
  );
}
